import { useDraggable } from '@dnd-kit/core';
import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { IconIndicator } from '@components/icons/indicator';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { getScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { FieldDropzone } from './field-dropzone';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { SmallDragHandle } from '../drag-handle';
import { FieldEditContainer } from '../field-edit';
import { FieldEditHeader, FieldEditHeaderActions } from '../field-edit-header';
import { FieldConfig } from '../field-registry';

type Config = FieldConfig<'separator'>;

const FormBuilderSeparator: Config['Component'] = ({ field }) => {
  const {
    store: { actions, useIsFieldSelected },
  } = useFormBuilderContext();
  const isSelected = useIsFieldSelected(field.id);

  const disallowDrag = field.builderProps?.canReorder === false;

  const draggable = useDraggable({
    id: `selected-${field.id}`,
    data: {
      field,
    },
    disabled: disallowDrag,
  });

  if (draggable.isDragging) {
    return null;
  }

  return (
    <FieldDropzone field={field}>
      <SeparatorContainer
        id={`field-${field.id}`}
        ref={draggable.setNodeRef}
        data-testid="formbuilder-separator-container"
        {...draggable.attributes}
        {...draggable.listeners}
        data-selected={isSelected}
        onClick={(e) => {
          e.stopPropagation();
          actions.selectField(field.id);
        }}
      >
        <AnimatePresence initial={false}>
          {isSelected ? (
            <FieldEditHeader
              dragHandle={<SmallDragHandle />}
              field={field}
              content={<Separator />}
            />
          ) : (
            <Stack space={2} orientation="horizontal" align="center" justify="space-between">
              <div data-handle>
                <SmallDragHandle />
              </div>
              <Separator />
              <FieldEditHeaderActions field={field} />
            </Stack>
          )}
        </AnimatePresence>
      </SeparatorContainer>
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Separator',
  },
});

export const separatorConfig: Config = {
  kind: 'separator',
  title: t('title'),
  group: 'structure',
  propertiesTabs: [conditionsTab],
  TreeDisplay: Separator,
  Display: Separator,
  Component: FormBuilderSeparator,
  Icon: IconIndicator,
};

const SeparatorContainer = styled(FieldEditContainer)({
  transition: 'background 0.2s ease-out',
  overflow: 'hidden',

  '[data-handle="true"]': {
    transition: 'all 0.5s ease-out',
    visibility: 'hidden',
    margin: `-${pxToRem(4)}`,
    marginLeft: '-50%',
    marginRight: '10%',
    opacity: 0,
  },

  '[data-c="edit-header-actions"]': {
    transition: 'all 0.5s ease-out',
    visibility: 'hidden',
    margin: `-${pxToRem(4)}`,
    marginRight: '-50%',
    opacity: 0,
  },

  '&:hover': {
    '[data-c="edit-header-actions"]': {
      visibility: 'visible',
      transition: 'all 0.2s ease-out',
      marginRight: 0,
      opacity: 1,
    },

    '[data-handle="true"]': {
      visibility: 'visible',
      transition: 'all 0.2s ease-out',
      marginLeft: 0,
      marginRight: 0,
      opacity: 1,
    },
  },

  '&[data-selected="true"]': {
    padding: `${pxToRem(3)} ${pxToRem(7)}`,
    '[data-c="edit-header-actions"]': {
      margin: 0,
      visibility: 'visible',
      opacity: 1,
    },
  },
});
